/**
 * Fine-grained BI permission codes (stored in enabled_modules JSON alongside coarse shell keys).
 * PHP mirror: App\Support\BiModules::GRANULAR_KEYS
 */
import { ADMIN_SECTION_DEFINITIONS } from './adminSectionRegistry';

export const GRANULAR_MODULE_KEYS = [
    'm.100',
    'm.101',
    'm.102',
    'm.103',
    'm.104',
    'm.105',
    'm.108',
    'm.110',
    'm.121',
    'm.1211',
    'm.1212',
    'm.1213',
    'm.125',
    'm.126',
    'm.127',
    'm.122',
    'm.123',
    'm.124',
    'm.130',
    'm.131',
    'm.200',
    'm.201',
    'm.202',
    'm.203',
    'm.204',
    'm.205',
    'm.206',
    'm.207',
    'm.208',
    'm.209',
    'm.210',
    'm.211',
    'm.212',
    'm.213',
] as const;

export type GranularModuleKey = (typeof GRANULAR_MODULE_KEYS)[number];

/** Labels for granular codes not mapped to ADMIN_SECTION_DEFINITIONS (future modules). */
export const GRANULAR_EXTRA_LABELS: Record<
    'm.100' | 'm.121' | 'm.122' | 'm.123' | 'm.124' | 'm.130' | 'm.131',
    { labelHr: string; labelEn: string }
> = {
    'm.100': { labelHr: 'Administracija — početni izbornik', labelEn: 'Administration — landing' },
    'm.121': { labelHr: 'Računi (admin)', labelEn: 'Invoices (admin)' },
    'm.122': { labelHr: 'Banke (admin)', labelEn: 'Banks (admin)' },
    'm.123': { labelHr: 'Leasing (admin)', labelEn: 'Leasing (admin)' },
    'm.124': { labelHr: 'Pozajmice knjiga (admin)', labelEn: 'Loans book (admin)' },
    'm.130': { labelHr: 'Bank Ready Pack (granula)', labelEn: 'Bank Ready Pack (granule)' },
    'm.131': { labelHr: 'Dokumentacija / DMS (granula)', labelEn: 'Documentation / DMS (granule)' },
};

/** Checkbox label for coarse shell keys + granular m.* codes in assignment UI */
export function assignmentKeyLabel(key: string, locale: 'hr' | 'en'): string {
    const extra = GRANULAR_EXTRA_LABELS[key as keyof typeof GRANULAR_EXTRA_LABELS];
    if (extra) {
        return locale === 'en' ? `${key} · ${extra.labelEn}` : `${key} · ${extra.labelHr}`;
    }

    const gran = /^m\.(\d+)$/.exec(key);
    if (gran) {
        const id = gran[1];
        const def = ADMIN_SECTION_DEFINITIONS.find((row) => row.id === id);
        if (def) {
            const title = locale === 'en' ? def.labelEn : def.labelHr;

            return `${key} · ${title}`;
        }
    }

    return key;
}

/** All keys shown when editing company/user module assignments (shell + granular). */
export function allAssignmentModuleKeys(coarseKeys: readonly string[]): string[] {
    return [...coarseKeys, ...GRANULAR_MODULE_KEYS];
}

/** Coarse BI_MODULE_KEYS order first, then granular codes by numeric suffix. */
export function orderedAssignmentKeys(coarseKeys: readonly string[]): string[] {
    const merged = allAssignmentModuleKeys(coarseKeys);

    return merged.sort((left, right) => {
        const rank = (key: string): number => {
            if (key.startsWith('m.')) {
                const parsed = Number.parseInt(key.slice(2), 10);

                return 1000 + (Number.isFinite(parsed) ? parsed : 0);
            }
            const idx = coarseKeys.indexOf(key);

            return idx >= 0 ? idx : 999;
        };

        return rank(left) - rank(right);
    });
}
